
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { LabelText } from "../Utils/Input";
import { Card, CardBody, Col, Row, Form, FormGroup, Container, CardHeader, Button, TabContent, TabPane, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Label, Input } from 'reactstrap';
import { ToastContainer, toast } from "react-toastify";
import BreadCrumb from "../Common/BreadCrumb";
import { Loader } from "../Utils/Loader";
import { useDispatch } from "react-redux";
import { validationSchemaForUserRole } from "./Constant/validationSchema";
import { GenerateModulePermissionsTable } from "./GenerateModulePermissionsTable";
import { useSelector } from "react-redux";
import { initializePermissions } from "./Constant/InitializePermissions";
import { getUserPermissions } from "./Constant/getUserPermissions";
import { CRUD_PERMS } from "./Constant/constants";
import CommonButton from "../Utils/CommonButton";
import { createUserRole } from "../../slices/UserRoleManagement/CreateRole/thunk";
import { combineUserAndAdminPermissions } from "./Constant/combineUserAndAdminPermissions";
import { editUserRole } from "../../slices/UserRoleManagement/EditRole/thunk";
import { userRoleMap,DefaultRole } from "./Constant/constants";

export default function CreateEditUserRole({ selectedRow }) {
    const [loading, setLoading] = useState()
    const [modulePermissions, setModulePermissions] = useState([])
    const [openSubmodulePopover, setOpenSubmodulePopover] = useState(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isCreateUserRole = location.pathname.includes("/add-userrole")
    const isEditUserRole = location.pathname.includes("/edit-userrole")
    const userPermissions=isEditUserRole?selectedRow?.modules:[]
    const permissions = useSelector(
        (state) => state.permissions.permissions
    );
    const mergeSelectedAndAdminPermissions=isEditUserRole?combineUserAndAdminPermissions(userPermissions,permissions?.modules):[]

    const rawPermissions = useSelector(
        (state) => state.permissions.permissionsMap
    );

    const permissionsMap = new Map(
        rawPermissions.map(([key, value]) => [key, new Set(value)])
    );

    const { domain } = useSelector((state) => state.permissions.permissions)

    const initialValues = isCreateUserRole ? {
        rolename: "",
        modules: modulePermissions
    } : {
        rolename: selectedRow?.roletype?.toLowerCase()===DefaultRole?userRoleMap.get(selectedRow?.rolename):selectedRow?.rolename,
        modules: mergeSelectedAndAdminPermissions
    }


    const handleFormSubmit = (values) => {
        const finalUserPermission = getUserPermissions(values.modules)
        const jsonObj = {
            rolename: values?.rolename,
            modules: finalUserPermission,
            domain: domain
        }
        if(isCreateUserRole){
            dispatch(createUserRole(jsonObj, setLoading, navigate))
        }
        else{
            dispatch(editUserRole(jsonObj,values.rolename,setLoading,navigate))
        }
    }

    useEffect(() => {
        if (isCreateUserRole && permissions?.modules) {
            setModulePermissions(initializePermissions(permissions.modules, "create"));
        }
    }, [permissions]);


    function camelCaseToTitle(str) {
        if (!str) return "";
        const result = str.replace(/([A-Z])/g, " $1");
        return result.charAt(0).toUpperCase() + result.slice(1);
    }
    return (<React.Fragment>
        <div id='csms' className='page-content bg-white'>
            <Container fluid>
                <BreadCrumb title={"User Roles"} />
                <ToastContainer position='top-center' />
                <Row>
                    <Col lg={12}>
                        <CardBody>
                            <h1 className="fs-18 fw-semibold">{isEditUserRole ? "Edit Role" : "Create A New Role"}</h1>
                            <div >
                                <Formik
                                    enableReinitialize={true}
                                    validationSchema={validationSchemaForUserRole}
                                    initialValues={initialValues}
                                    onSubmit={(values) => {
                                        handleFormSubmit(values)
                                    }}
                                >
                                    {({ handleChange, handleBlur, handleSubmit, errors, touched, values, setFieldValue, setFieldTouched, resetForm, isValid, dirty }) => {
                                        //Toggle Module
                                        const toggleModule = (modIdx) => {
                                            const newModules = values.modules.map((mod, idx) => {
                                                if (idx !== modIdx) return mod;
                                                const newIsSelected = !mod.isSelected;
                                                return {
                                                    ...mod,
                                                    isSelected: newIsSelected,
                                                    submodules: mod.submodules.map((s) => ({
                                                        ...s,
                                                        isSelected: newIsSelected ? s.isSelected : false,
                                                        permissions: newIsSelected ? s.permissions : [],
                                                    })),
                                                };
                                            });
                                            setFieldValue("modules", newModules);
                                        };

                                        //Toggle Submodule
                                        const toggleSubmodule = (modIdx, subIdx) => {
                                            const newModules = values.modules.map((mod, idx) => {
                                                if (idx !== modIdx) return mod;
                                                return {
                                                    ...mod,
                                                    submodules: mod.submodules.map((s, sIdx) => {
                                                        const allSubModulePermissions = [
                                                            ...(permissionsMap.get(
                                                                `${mod.module.toLowerCase()}-${s.submodule.toLowerCase()}`
                                                            ) || []),
                                                        ];
                                                        const otherPermissions = allSubModulePermissions.filter(
                                                            (p) => !CRUD_PERMS.includes(camelCaseToTitle(p))
                                                        );
                                                        const permissions = !s.isSelected ? otherPermissions.includes("list") ? ["list"] : [] : [] // Need to make sure List Permissions is always thr when the submodule is check
                                                        return sIdx === subIdx
                                                            ? { ...s, isSelected: !s.isSelected, permissions: permissions }
                                                            : s
                                                    }),
                                                };
                                            });
                                            setFieldValue("modules", newModules);
                                        };

                                        //Toggle Permission
                                        const togglePermission = (modIdx, subIdx, perm) => {
                                            const newModules = values.modules.map((mod, idx) => {
                                                if (idx !== modIdx) return mod;
                                                return {
                                                    ...mod,
                                                    submodules: mod.submodules.map((s, sIdx) =>
                                                        sIdx === subIdx
                                                            ? {
                                                                ...s,
                                                                permissions: s.permissions.includes(perm)
                                                                    ? s.permissions.filter((p) => p !== perm)
                                                                    : [...s.permissions, perm],
                                                            }
                                                            : s
                                                    ),
                                                };
                                            });
                                            setFieldValue("modules", newModules);
                                        };

                                        //Toggle AllowAllmodule
                                        const toggleAllowAllPermission = (modIdx, subIdx, currentValue) => {
                                            const newModules = values.modules.map((mod, idx) => {
                                                if (idx !== modIdx) return mod;
                                                return {
                                                    ...mod,
                                                    submodules: mod.submodules.map((sub, sIdx) => {
                                                        if (sIdx !== subIdx) return sub;

                                                        const key = `${mod.module.toLowerCase()}-${sub.submodule.toLowerCase()}`;
                                                        const permsSet = permissionsMap.get(key) || new Set();

                                                        return {
                                                            ...sub,
                                                            permissions: !currentValue ? [] : [...permsSet],
                                                        };
                                                    }),
                                                };
                                            });
                                            setFieldValue("modules", newModules);
                                        };
                                        return <Form className='needs-validation' onSubmit={handleSubmit}>
                                            <Row>
                                                {loading && (<Loader />)}
                                                <Col lg={12}>
                                                    <div>
                                                        <FormGroup className="my-4">
                                                            <LabelText
                                                                htmlFor={"rolename"}
                                                                classLabelName={"ms-0 rv-form-label"}
                                                                className={"text-danger"}
                                                                displayText={"Role Name"}
                                                                important={"*"}
                                                            />

                                                            <LabelText
                                                                htmlFor={"rolename"}
                                                                classLabelName={touched.rolename && errors.rolename ? "d-block ms-0 rv-sub-label rv-label-danger" : "d-block ms-0 rv-sub-label rv-label-muted"}
                                                                displayText={"Enter Role Name eg. Business User"}
                                                            />
                                                            <Input
                                                                id="rolename"
                                                                value={values.rolename}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                            />
                                                        </FormGroup>

                                                        <div className="my-4">
                                                            <h2 className="fs-16 fw-semibold">Access Privilege Configurations</h2>
                                                            <h3 className="fs-12 rv-label-muted">Choose the modules, screens, and menu options this role has access to</h3>
                                                        </div>

                                                        <div className="my-4">
                                                            <GenerateModulePermissionsTable modules={values.modules} permissionsMap={permissionsMap} toggleModule={toggleModule} toggleSubmodule={toggleSubmodule} togglePermission={togglePermission} toggleAllowAllPermission={toggleAllowAllPermission} openSubmodulePopover={openSubmodulePopover} setOpenSubmodulePopover={setOpenSubmodulePopover}/>
                                                        </div>
                                                        <div className="my-4">
                                                            <div className="d-flex justify-content-end gap-2">
                                                                <CommonButton
                                                                    outline={true}
                                                                    className={
                                                                        "btn-label rv-secondary-btn rounded-1 px-1 ps-3 py-1  w-md-auto text-nowrap"
                                                                    }
                                                                    color={"main-color"}
                                                                    type={"button"}
                                                                    buttonAction={() => {
                                                                        navigate("/userroles")
                                                                    }}
                                                                    buttonText={"Cancel"}
                                                                    icon={
                                                                        <i className="ri-close-fill label-icon align-middle fs-16 me-1" style={{ transform: "scaleX(-1)", borderLeft: "1px solid" }}>
                                                                        </i>
                                                                    }
                                                                    btnTxtClsName={"px-2 ps-3 ms-2 me-1"}
                                                                />

                                                                <CommonButton
                                                                    color={"secondary"}
                                                                    type="submit"
                                                                    buttonText={"Submit"}
                                                                    disabled={!isValid || !dirty}
                                                                    icon={<i className="ri-checkbox-circle-line label-icon align-middle fs-16 me-1"></i>}
                                                                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1  w-md-auto text-nowrap"
                                                                    btnTxtClsName={"px-2 ps-3 ms-2 me-1"}
                                                                />
                                                            </div>

                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Form>
                                    }}
                                </Formik>
                            </div>

                        </CardBody>
                    </Col>
                </Row>
            </Container>
        </div>
    </React.Fragment>)
}